/**
 * Client-side fallback for comparison summary (mirrors backend logic).
 */
import { formatFitScoreBadge } from "./matchScoreUtils";

function formatPrice(price) {
  const n = Number(price);
  return Number.isFinite(n) ? `$${n.toLocaleString()}` : "—";
}

function getMatchPercent(product) {
  const raw = product?.matchScore ?? product?.score ?? product?.match;
  if (raw == null || !Number.isFinite(Number(raw))) return null;
  return Math.round(Number(raw));
}

function getWinsForProduct(productId, comparisonTable) {
  const wins = [];
  (comparisonTable?.rows || []).forEach((row) => {
    const col = comparisonTable?.columns?.[row.bestIndex];
    if (!col?.id || row.bestIndex == null || row.bestIndex < 0) return;
    if (String(col.id) === String(productId)) {
      wins.push(row.label || row.key);
    }
  });
  return wins;
}

export function buildComparisonSummaryText({
  products = [],
  searchParams = null,
  comparisonTable = null,
  topMatchProductId = null,
  valuePickProductId = null,
}) {
  if (!products.length) return "";

  const byId = new Map(products.map((p) => [String(p._id || p.id), p]));
  const top =
    byId.get(String(topMatchProductId)) ||
    products.find((p) => Number(p.rank) === 1) ||
    products[0];
  const value = byId.get(String(valuePickProductId)) || top;
  const topId = String(top._id || top.id);
  const valueId = String(value._id || value.id);
  const isValuePick = topId !== valueId;

  const useCase = searchParams?.useCase
    ? String(searchParams.useCase).toLowerCase()
    : "";
  const useCasePhrase = useCase ? ` for ${useCase}` : "";
  const topScore = getMatchPercent(top);
  const topWins = getWinsForProduct(topId, comparisonTable).slice(0, 3);
  const valueWins = getWinsForProduct(valueId, comparisonTable).slice(0, 3);

  if (isValuePick) {
    const topLead =
      topScore != null
        ? `${top.name} leads on overall fit (${formatFitScoreBadge(topScore)})`
        : `${top.name} is your strongest overall match`;
    const valueLead = `${value.name} is the better buy at ${formatPrice(value.price)}`;
    const topSpec =
      topWins.length > 0 ? ` ${top.name} wins on ${topWins.join(", ")}.` : "";
    const valueSpec =
      valueWins.length > 0
        ? ` ${value.name} wins on ${valueWins.join(", ")}.`
        : "";
    return `${topLead}${useCasePhrase}, while ${valueLead}.${topSpec}${valueSpec} Use the side-by-side rows below to weigh each trade-off.`;
  }

  if (topScore != null) {
    const spec =
      topWins.length > 0 ? ` It leads on ${topWins.join(", ")}.` : "";
    return `${top.name} is the strongest pick in this set (${formatFitScoreBadge(topScore)})${useCasePhrase}.${spec} Review the detailed comparison below.`;
  }

  const spec =
    topWins.length > 0 ? ` It leads on ${topWins.join(", ")}.` : "";
  return `${top.name} ranks first among these options${useCasePhrase}.${spec} Review the detailed comparison below.`;
}

function buildTradeoffsFromTable(products = [], comparisonTable = null) {
  const winsByIndex = new Map();
  const lossesByIndex = new Map();

  (comparisonTable?.rows || []).forEach((row) => {
    const bestIdx = row.bestIndex;
    if (bestIdx == null || bestIdx < 0) return;

    const label = row.label || row.key || "spec";
    winsByIndex.set(bestIdx, [...(winsByIndex.get(bestIdx) || []), label]);

    (row.values || []).forEach((value, idx) => {
      if (idx === bestIdx || value == null || value === "—") return;
      lossesByIndex.set(idx, [...(lossesByIndex.get(idx) || []), label]);
    });
  });

  return products.map((product, index) => {
    const productId = String(product._id || product.id);
    const wins = winsByIndex.get(index) || [];
    const losses = lossesByIndex.get(index) || [];

    return {
      productId,
      pros: wins.slice(0, 4).map((item) => `Leads on ${item}`),
      cons: losses.slice(0, 3).map((item) => `Behind on ${item}`),
    };
  });
}

export function buildComparisonSummaryFallback({
  products = [],
  searchParams = null,
  comparisonTable = null,
  topMatchProductId = null,
  valuePickProductId = null,
}) {
  if (!products.length) {
    return {
      winnerProductId: null,
      summary: "",
      tradeoffs: [],
      source: "fallback",
    };
  }

  const winnerProductId =
    topMatchProductId ||
    String(
      products.find((p) => Number(p.rank) === 1)?._id ||
        products[0]?._id ||
        products[0]?.id ||
        "",
    ) ||
    null;

  return {
    winnerProductId,
    summary: buildComparisonSummaryText({
      products,
      searchParams,
      comparisonTable,
      topMatchProductId,
      valuePickProductId,
    }).slice(0, 800),
    tradeoffs: buildTradeoffsFromTable(products, comparisonTable),
    source: "fallback",
  };
}
